/**
 * @package FlorenceEGI — EGI — Home Supreme Islands Utils
 * @author Padmin D. Curtis (AI Partner OS3.0) for Fabio Cherici
 * @version 1.0.0
 * @date 2026-04-22
 * @purpose Safe DOM helpers. No innerHTML with untrusted data. If HTML is ever
 *          needed, callers must sanitize with DOMPurify.sanitize().
 */

export function el<K extends keyof HTMLElementTagNameMap>(
    tag: K,
    opts?: {
        className?: string;
        text?: string;
        attrs?: Record<string, string>;
        children?: (Node | null | undefined)[];
    },
): HTMLElementTagNameMap[K] {
    const node = document.createElement(tag);
    if (opts?.className) node.className = opts.className;
    if (opts?.text !== undefined) node.textContent = opts.text;
    if (opts?.attrs)
        for (const [k, v] of Object.entries(opts.attrs)) node.setAttribute(k, v);
    if (opts?.children)
        for (const c of opts.children) if (c) node.appendChild(c);
    return node;
}

export function clear(node: HTMLElement): void {
    while (node.firstChild) node.removeChild(node.firstChild);
}

export function formatPrice(value: number | null | undefined): string {
    if (value == null) return "—";
    return new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
    }).format(value);
}

export type ImageVariants = {
    src?: string;
    thumbnail?: string;
    mobile?: string;
    tablet?: string;
    desktop?: string;
    large?: string;
    original?: string;
};

export function lazyImg(
    src: string,
    alt: string,
    opts?: { width?: number; height?: number; sizes?: string; srcset?: string },
): HTMLImageElement {
    const img = document.createElement("img");
    img.loading = "lazy";
    img.decoding = "async";
    img.src = src;
    img.alt = alt;
    if (opts?.width) img.width = opts.width;
    if (opts?.height) img.height = opts.height;
    if (opts?.sizes) img.sizes = opts.sizes;
    if (opts?.srcset) img.srcset = opts.srcset;
    return img;
}

/**
 * Build srcset string from ImageVariants object using pixel widths
 * matching config/image-optimization.php (egi variants).
 */
export function buildSrcset(v: ImageVariants | null | undefined): string {
    if (!v) return "";
    const parts: string[] = [];
    if (v.thumbnail) parts.push(`${v.thumbnail} 150w`);
    if (v.mobile) parts.push(`${v.mobile} 400w`);
    if (v.tablet) parts.push(`${v.tablet} 600w`);
    if (v.desktop) parts.push(`${v.desktop} 800w`);
    if (v.large) parts.push(`${v.large} 1200w`);
    return parts.join(", ");
}

/**
 * Pick the best src for a given container size (display px, not CSS px)
 * from ImageVariants. Falls back gracefully down the chain.
 */
export function pickVariant(
    v: ImageVariants | null | undefined,
    targetPx: number,
): string | null {
    if (!v) return null;
    if (targetPx <= 150 && v.thumbnail) return v.thumbnail;
    if (targetPx <= 400 && v.mobile) return v.mobile;
    if (targetPx <= 600 && v.tablet) return v.tablet;
    if (targetPx <= 800 && v.desktop) return v.desktop;
    if (v.large) return v.large;
    return v.desktop ?? v.tablet ?? v.mobile ?? v.thumbnail ?? v.original ?? v.src ?? null;
}
